import React, { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { BASE_URL } from '../config';

export interface UsuarioConectado {
  userId: number;
  username: string;
  nombre: string;
  rol: string;
}

interface SocketContextType {
  socket: Socket | null;
  connected: boolean;
  usuariosConectados: UsuarioConectado[];
  count: number;
  cajaUpdatedAt?: number;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token, user } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [usuariosConectados, setUsuariosConectados] = useState<UsuarioConectado[]>([]);
  const [cajaUpdatedAt, setCajaUpdatedAt] = useState<number>(0);

  useEffect(() => {
    if (!token || !user) {
      setSocket((prev) => {
        if (prev) {
          prev.disconnect();
        }
        return null;
      });
      setConnected(false);
      setUsuariosConectados([]);
      return;
    }

    const socketUrl =
      BASE_URL.startsWith('http') ? BASE_URL : window.location.origin;
    const newSocket = io(socketUrl, {
      auth: { token },
      path: '/socket.io',
    });

    newSocket.on('connect', () => {
      setConnected(true);
      newSocket.emit('set-user-data', { nombre: user.nombre });
    });

    newSocket.on('disconnect', () => {
      setConnected(false);
    });

    newSocket.on('usuarios-conectados', (data: { count: number; usuarios: UsuarioConectado[] }) => {
      setUsuariosConectados(data.usuarios || []);
    });

    newSocket.on('caja-updated', (data: any) => {
      setCajaUpdatedAt(Date.now());
    });

    newSocket.on('connect_error', () => {
      setConnected(false);
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
      setSocket(null);
      setConnected(false);
      setUsuariosConectados([]);
    };
  }, [token, user]);

  return (
    <SocketContext.Provider
      value={{
        socket,
        connected,
        usuariosConectados,
        count: usuariosConectados.length,
        cajaUpdatedAt,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket debe usarse dentro de SocketProvider');
  }
  return context;
};
